import { GlobalEvents } from '@highlight/ipc'
import { useSetSidepanelHotkeyOpened, useSidepanelSide } from '../../../atoms'
import { useCallback, useEffect, useState } from 'react'
import { useSidepanel, useSidepanelPeekSource } from '@renderer/overlay/atoms'
import { trackSidepanelOpened, trackSidepanelClosed } from '@renderer/overlay/utils/track-sidepanel'
import { usePeekAffordance } from '@renderer/overlay/hooks/use-peek-affordance'
import { useEdgeProximity } from '@renderer/overlay/hooks/use-edge-proximity'
import { useEdgeApproachX } from '@renderer/overlay/hooks/use-edge-trigger'
import { EdgePeekButton } from '../../edge-peek-button'
import { setOverlayInteractive } from '@highlight/ui'

export function SidepanelPeekTrigger() {
  const [sidepanelState, setSidepanelState] = useSidepanel()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()
  const isVisible = sidepanelState === 'peeking'
  const isOpen = sidepanelState === 'open'
  const [isHovered, setIsHovered] = useState(false)
  const peekSource = useSidepanelPeekSource()
  const side = useSidepanelSide()
  // The chevron's show/hide is driven by cursor proximity to the panel's edge,
  // over the settings-derived zone (0 at low sensitivities, where the trigger
  // only appears via the native peek pin below).
  const { peekTriggerEnabled, approachZoneRatio } = usePeekAffordance()

  // Track proximity continuously while the panel is closed — including during
  // peek. Keeping the value live (instead of resetting it to 0 when peek pins
  // the trigger in) means it's still accurate when peek ends, so the trigger
  // settles smoothly to the cursor instead of dropping to hidden and bouncing
  // back in on the next mouse move.
  const proximityEnabled = !isOpen && approachZoneRatio > 0
  const { proximity } = useEdgeProximity({ zoneRatio: approachZoneRatio, side, enabled: proximityEnabled })

  // A peek is "proximity-driven" only when the cursor is actually at the edge:
  // an edge peek (not a far-from-edge Mini peek) with a live approach zone (not
  // low sensitivity, where the zone is 0) and outside onboarding (which force-
  // pins to teach the gesture). Such a peek lets proximity own the chevron's
  // slide, so moving away retracts it immediately and moving back brings it in,
  // instead of waiting on the close timer.
  const proximityDriven = peekSource === 'edge' && approachZoneRatio > 0

  // Pin the chevron fully in for every other peek. Also pin a proximity-driven
  // one while the cursor is on it: hovering cancels the auto-hide timer, but a
  // proximity slide could otherwise move it out from under a near-stationary
  // cursor (a transform move fires no mouseleave), stranding the peek open with
  // the hide cancelled. Pinning keeps it put so the only way off it is a real
  // mouseleave, which restarts the hide.
  const pinnedOpen = isVisible && (!proximityDriven || isHovered)
  const triggerX = useEdgeApproachX({ proximity, side, pinnedOpen })

  // The trigger only fires a real mouseleave (to restart the auto-hide) while
  // it's rendered and slid in. Hovering it emits sidepanel:cancelHide, which
  // clears the close timer — so if it disappears without a mouseleave, the peek
  // would stay stuck open. That happens when it becomes invisible (mouse left the
  // overlay window, or the panel opened) or when it unmounts entirely (peek
  // trigger toggled off in settings) while hovered. In all those cases restart
  // the hide ourselves.
  const triggerInteractable = isVisible && peekTriggerEnabled

  useEffect(() => {
    if (!triggerInteractable && isHovered) {
      setIsHovered(false)
      GlobalEvents.emit('sidepanel:restartHide')
    }
  }, [triggerInteractable, isHovered])

  const handleClick = useCallback(() => {
    if (sidepanelState === 'hidden' || sidepanelState === 'peeking') {
      trackSidepanelOpened('trigger')
      setHotkeyOpened(true)
      setSidepanelState('open')
    } else {
      trackSidepanelClosed('trigger')
      setOverlayInteractive(false)
      setHotkeyOpened(false)
      setSidepanelState('hidden')
    }
  }, [sidepanelState, setSidepanelState, setHotkeyOpened])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
    GlobalEvents.emit('sidepanel:cancelHide')
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
    GlobalEvents.emit('sidepanel:restartHide')
  }, [])

  if (isOpen) return null

  // The peek chevron is opt-out. Push-to-edge still opens the panel when hidden.
  if (!peekTriggerEnabled) return null

  return (
    <EdgePeekButton
      x={triggerX}
      side={side}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )
}
